import React, { memo } from 'react';
import { Loader } from '@mantine/core';
import styled from 'styled-components';
import { ReactFlow } from '@xyflow/react';

import { CapChartContent } from './CapChartContent';
import { useCapChartController } from './useCapChartController';
import { CAP_CHART_SURFACE_STYLES } from './chartSurface.styles';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { generatePath, useParams } from 'react-router-dom';
import { DEAL_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import {
	useTranslationCapChart,
	useTranslationCapTable,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

const fitViewOptions = {
	padding: 0.1,
	duration: 200,
};

const CapChart = () => {
	const tCapTable = useTranslationCapTable();
	const tCapChart = useTranslationCapChart();
	const { dealId } = useParams();

	const {
		nodes,
		edges,
		nodeTypes,
		edgeTypes,
		isDataLoaded,
		onEdgeMouseEnter,
		onEdgeMouseLeave,
		onNodesChange,
		onEdgesChange,
		onConnect,
		connectionLineType,
		onSave,
		onReset,
	} = useCapChartController();

	// this can move to CapChartPage when in page feature flag is removed
	useBreadcrumbs([
		{
			name: tCapTable('title'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_TABLE}`, {
				dealId: dealId ?? '',
			}),
		},
		{
			name: tCapChart('chart_title'),
			url: generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.CAP_CHART}`, { dealId: dealId ?? '' }),
		},
	]);

	return isDataLoaded ? (
		<StyledReactFlow
			snapToGrid
			onEdgeMouseEnter={onEdgeMouseEnter}
			onEdgeMouseLeave={onEdgeMouseLeave}
			snapGrid={[50, 50]}
			nodes={nodes}
			edges={edges}
			nodeTypes={nodeTypes}
			edgeTypes={edgeTypes}
			onNodesChange={onNodesChange}
			onEdgesChange={onEdgesChange}
			onConnect={onConnect}
			connectionLineType={connectionLineType}
			fitView
			fitViewOptions={fitViewOptions}
			nodesDraggable
			elementsSelectable
			nodesConnectable={false}
		>
			<CapChartContent
				hasElements={!!nodes.length || !!edges.length}
				onSave={onSave}
				onReset={onReset}
			/>
		</StyledReactFlow>
	) : (
		<Loader />
	);
};

const Memoized = memo(CapChart);

export { Memoized as CapChart };

/** The group-structure surface: node sizing the layout depends on, plus the hover treatment. */
const StyledReactFlow = styled(ReactFlow)(CAP_CHART_SURFACE_STYLES);
